import { createFileRoute, Outlet, useNavigate, useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { CustomerLogo } from '@nineeng/form-components';
import { useMemo } from 'react';
import { Tabs, Badge, Spinner } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import {
	getCompany,
	getMyCompanies,
	getCompanyTasks,
	getCompanyApprovals,
	getCompanyDocuments,
	getEventTasksByGroup,
	getPreAccessTasks,
} from 'src/api/company';
import { getCustomerThemeSettings } from 'src/api/admin/settings';

export const Route = createFileRoute('/company_/$groupId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();

	// Determine active tab from current route
	const matches = useMatches();
	const lastMatch = matches[matches.length - 1];
	const selectedSegment = lastMatch?.pathname?.replace(`/company/${groupId}/`, '')?.split('/')[0] as
		| 'information'
		| 'events'
		| 'users'
		| 'tasks'
		| 'documents'
		| 'projects'
		| 'approvals';

	// Fetch group data
	const { data: group, isLoading: groupLoading } = useQuery({
		queryKey: ['company-group', groupId],
		queryFn: () => getCompany(groupId),
		enabled: !userLoading,
	});

	// Validate user has access to this group
	const { data: myGroups } = useQuery({
		queryKey: ['company-groups'],
		queryFn: () => getMyCompanies(),
		enabled: !userLoading,
	});

	// Pre-access check
	const { data: preAccessTasks } = useQuery({
		queryKey: ['company-pre-access-tasks', groupId],
		queryFn: () => getPreAccessTasks(groupId),
		enabled: !userLoading && !!group,
	});

	// Feature flags
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	useBreadcrumbLevel(0, group?.name || '');

	// Fetch tab badge data
	const { data: tasksResponse } = useQuery({
		queryKey: ['company-tasks', groupId],
		queryFn: () => getCompanyTasks(groupId),
		enabled: !userLoading && !!group,
	});

	const { data: approvalsResponse } = useQuery({
		queryKey: ['company-approvals', groupId],
		queryFn: () => getCompanyApprovals(groupId),
		enabled: !userLoading && !!group,
	});

	const { data: documents } = useQuery({
		queryKey: ['company-documents', groupId],
		queryFn: () => getCompanyDocuments(groupId),
		enabled: !userLoading && !!group,
	});

	const { data: eventTasks } = useQuery({
		queryKey: ['company-event-tasks', groupId],
		queryFn: () => getEventTasksByGroup(groupId),
		enabled: !userLoading && !!group,
	});

	const { data: themeSettings } = useQuery({
		queryKey: ['customer-theme-settings'],
		queryFn: () => getCustomerThemeSettings(),
		enabled: !userLoading,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions);
	const usesSubContracting = featureFlags?.USES_SUBCONTRACTING || false;

	const tasks = tasksResponse?.data || [];
	const groupApprovals = approvalsResponse?.data || [];
	const groupDocuments = documents?.data || [];
	const eventTasksList = eventTasks?.data || [];

	const tasksTodo = tasks?.filter(({ status }) => status === 'todo');
	const eventTasksTodo = eventTasksList?.filter(({ status }) => status === 'todo');
	const approvalsToDo = groupApprovals?.filter((t) => t.status === 'todo');
	const hasDocumentsInReview = groupDocuments?.some((doc) => doc.status === 'inReview');
	const isBlocked = group?.isBlocked;

	const { tabs, tabIndexPerTabId } = useMemo(() => {
		const builtTabs = [
			{
				id: 'information',
				label: 'Information',
			},
			{
				id: 'tasks',
				label: 'Tasks',
				error: tasksTodo.length > 0 || eventTasksTodo.length > 0,
				errorContent: tasksTodo.length + eventTasksTodo.length,
			},
			...(verifyPermission(PERMISSIONS.companyUsersView)
				? [
						{
							id: 'users',
							label: 'Users',
						},
					]
				: []),
			...(usesSubContracting && verifyPermission(PERMISSIONS.companyProjectsView)
				? [
						{
							id: 'projects',
							label: 'Projects',
						},
					]
				: []),
			...(verifyPermission(PERMISSIONS.companyEventsView)
				? [
						{
							id: 'events',
							label: 'Events',
						},
					]
				: []),
			...(verifyPermission(PERMISSIONS.companyDocumentsView)
				? [
						{
							id: 'documents',
							label: 'Documents',
						},
					]
				: []),
			...(verifyPermission(PERMISSIONS.companyTasksView)
				? [
						{
							id: 'approvals',
							label: 'Approvals',
							error: approvalsToDo.length > 0,
							errorContent: approvalsToDo.length,
						},
					]
				: []),
		];

		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => ({
				...acc,
				[cur.id]: index,
			}),
			{} as Record<string, number>,
		);

		return { tabs: builtTabs, tabIndexPerTabId };
	}, [approvalsToDo.length, tasksTodo.length, eventTasksTodo.length, verifyPermission, usesSubContracting]);

	if (userLoading || groupLoading) {
		return null;
	}

	if (!authenticatedUser || !group) {
		return null;
	}

	// Check if user has access to this group
	if (myGroups && !myGroups.find((g) => g.id === groupId)) {
		navigate({ to: '/company', replace: true });
		return null;
	}

	// Pre-access redirect
	if (preAccessTasks && preAccessTasks?.tasks?.length > 0) {
		const firstTask = preAccessTasks?.tasks?.[0];
		if (firstTask?.status === 'todo') {
			// Check if user can do the task
			if (firstTask?.executePermission?.isAllowed) {
				navigate({
					to: '/company/verify/$groupId',
					params: { groupId },
					replace: true,
				});
				return null;
			} else {
				navigate({
					to: '/company/blocked/$groupId',
					params: { groupId },
					search: { reason: firstTask.executePermission?.denyReason || '' },
					replace: true,
				});
				return null;
			}
		}
	}

	const handleTabChange = (tabIndex: number) => {
		const tab = tabs[tabIndex];
		if (tab) {
			navigate({
				to: `/company/$groupId/${tab.id}`,
				params: { groupId },
			});
		}
	};

	return (
		<div className="px-4">
			<div className="relative pt-3">
				<div className="flex justify-between items-center gap-2">
					<div className="w-full">
						{themeSettings?.settings?.companyName && (
							<Typography variant="h4" className="mb-1">
								{themeSettings.settings.companyName}
							</Typography>
						)}

						{isBlocked && (
							<Alert severity="error" variant="filled">
								Your company is currently blocked. Employees will not have access to the site. Please contact an admin
								for more information.
							</Alert>
						)}
						{hasDocumentsInReview && (
							<Alert
								severity="error"
								variant="filled"
								sx={{
									marginBottom: 2,
									'& ul': { margin: 0, paddingLeft: 2 },
								}}
							>
								This company has one or more documents in review. Contact your administrator.
							</Alert>
						)}
						{approvalsToDo.length > 0 && (
							<Alert
								severity="warning"
								variant="filled"
								sx={{
									marginBottom: 2,
									'& ul': { margin: 0, paddingLeft: 2 },
								}}
							>
								This company has one or more pending approval tasks. Contact your administrator.
							</Alert>
						)}
					</div>
					{themeSettings?.settings?.logo?.link && (
						<div className="flex flex-col items-center justify-start">
							<CustomerLogo
								width="120"
								style={{
									maxHeight: 72,
								}}
								url={'/api/public/logo'}
								padding={20}
							/>
						</div>
					)}
				</div>
				<Tabs value={tabIndexPerTabId[selectedSegment]} onChange={(value) => handleTabChange(value)}>
					<Tabs.TabList>
						{tabs.map((tab) => (
							<Tabs.Tab
								key={tab.id}
								value={tabIndexPerTabId[tab.id]}
								label={
									tab.error ? (
										<div className="flex items-center gap-2">
											{tab.label}
											{tab.errorContent ? <Badge variant="error">{tab.errorContent}</Badge> : <Badge variant="error" />}
										</div>
									) : (
										tab.label
									)
								}
							/>
						))}
					</Tabs.TabList>
				</Tabs>
			</div>
			<Outlet />
		</div>
	);
}
